import { useEffect, useRef } from 'react';
import { auth } from '../firebase';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

const RECHECK_MS = 5 * 60 * 1000;

// The server owns the deadline. User activity, tab wakeups and token refreshes
// only re-read it; they never extend it. Preserve the existing offline guard.
export default function useSessionExpiry({ userId, businessId, enabled, onLogout }) {
  const logout = useRef(onLogout);
  logout.current = onLogout;
  useEffect(() => {
    if (!enabled || !userId || auth.currentUser?.uid !== userId) return;
    const user = auth.currentUser;
    const selectedBusiness = () => { try { return localStorage.getItem('businessId'); } catch { return businessId || null; } };
    const initialBusiness = selectedBusiness();
    const controller = new AbortController();
    let active = true, pending = false, loggingOut = false, timer;
    const current = () => active && auth.currentUser === user && selectedBusiness() === initialBusiness;
    const expire = async () => {
      if (!current() || loggingOut) return;
      loggingOut = true;
      try { await logout.current('session-expired', current); }
      finally { loggingOut = false; }
    };
    const check = async () => {
      if (!current() || pending) return;
      clearTimeout(timer);
      pending = true;
      let next = RECHECK_MS;
      try {
        if (navigator.onLine === false) return;
        const result = await nodeAxiosFirebase({ url: 'users/session', body: { businessId: businessId || null },
          expectedUserId: userId, signal: controller.signal, retry: false });
        if (!current()) return;
        if (result?.errorData?.data?.code === 'SESSION_EXPIRED') await expire();
        else if (Number.isFinite(result?.sessionExpiresAt)) {
          next = Math.min(RECHECK_MS, Math.max(1000, result.sessionExpiresAt - Date.now()));
        }
      } catch (_) { /* Server refusals remain enforced; a network error is not a logout. */ }
      finally {
        pending = false;
        if (current()) timer = setTimeout(check, next);
      }
    };
    const handleExpiry = event => {
      if (event.detail?.userId === userId && (event.detail.businessId || null) === (businessId || null)) {
        expire().catch(() => {});
      }
    };
    const resume = () => { if (document.visibilityState !== 'hidden') check(); };
    check();
    window.addEventListener('node:session-expired', handleExpiry);
    window.addEventListener('node:device-verified', check);
    window.addEventListener('node:session-policy-updated', check);
    window.addEventListener('online', check);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener('node:session-expired', handleExpiry);
      window.removeEventListener('node:device-verified', check);
      window.removeEventListener('node:session-policy-updated', check);
      window.removeEventListener('online', check);
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [userId, businessId, enabled]);
}
